/**
 * GradingResultsCard — Displays AI grading results.
 * LAUNCH:C6.P3
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import CGCLoopCTA from './CGCLoopCTA';
import GradeEstimateNotice from '../common/GradeEstimateNotice';

function getGradeColor(grade) {
  if (grade >= 9.0) return '#22c55e';
  if (grade >= 7.0) return '#84cc16';
  if (grade >= 5.0) return '#f59e0b';
  if (grade >= 3.0) return '#f97316';
  return '#ef4444';
}

function getGradeLabel(grade) {
  if (grade >= 9.8) return 'Near Mint/Mint';
  if (grade >= 9.0) return 'Near Mint';
  if (grade >= 7.0) return 'Very Fine';
  if (grade >= 5.0) return 'Fine';
  if (grade >= 3.0) return 'Very Good';
  if (grade >= 1.0) return 'Good';
  return 'Poor';
}

function getConfidenceLabel(confidence) {
  if (confidence > 90) return 'Excellent';
  if (confidence >= 70) return 'Good';
  if (confidence >= 50) return 'Fair';
  return 'Low';
}

const SEVERITY_COLORS = {
  NEGLIGIBLE: 'text-zinc-400',
  MINOR: 'text-yellow-400',
  MODERATE: 'text-orange-400',
  MAJOR: 'text-red-400',
  SEVERE: 'text-red-500',
};

export default function GradingResultsCard({ gradeResult, gradeValue, confidence, onRegrade, showCGCCta, vaultItemId }) {
  const [expandedDefect, setExpandedDefect] = useState(null);

  const grade = gradeValue ?? gradeResult?.grade_value;
  // ⛔ THE SERVED SHAPE IS A STRING, NOT A NUMBER. grade_value is Decimal in the auto-grade
  // schemas (AutoGradeResponse / AutoGradeEventSummary), and pydantic v2 serialises Decimal to
  // a JSON STRING -- verified: Decimal('9.4') -> "9.4", while a float field -> 9.4. So
  // `grade.toFixed(1)` threw "grade.toFixed is not a function" and took the whole card's render
  // with it. The unit tests never caught it because their fixtures passed NUMBERS, which is the
  // bug-encoding fixture class: the test asserted against a shape the API does not send.
  // Number() is a no-op on a real number, so this is correct for both callers.
  const gradeNum = grade == null || grade === '' || Number.isNaN(Number(grade)) ? null : Number(grade);
  const conf = confidence ?? gradeResult?.confidence;
  const defects = gradeResult?.defects || [];
  const color = getGradeColor(gradeNum);
  const label = getGradeLabel(gradeNum);
  const confLabel = getConfidenceLabel(conf);

  return (
    <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
      {/* Grade Display */}
      <div className="flex items-center gap-4">
        <div
          className="text-3xl font-bold"
          style={{ color }}
          aria-label={`Estimated grade: ${gradeNum ?? 'unavailable'} out of 10, ${label}`}
        >
          {gradeNum == null ? '--' : gradeNum.toFixed(1)}
        </div>
        <div>
          <p className="text-sm font-medium text-white" id="grade-label">{label}</p>
          <p className="text-xs text-zinc-500">Estimated Grade</p>
        </div>
      </div>

      {/* Confidence Meter */}
      {conf != null && (
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-zinc-500">Confidence</span>
            <span className="text-zinc-300">{conf}% - {confLabel}</span>
          </div>
          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${conf}%` }}
            />
          </div>
        </div>
      )}

      {/* Defect List */}
      <div>
        <p className="text-xs text-zinc-500 mb-2">
          {defects.length === 0 ? 'No defects detected' : `${defects.length} defect${defects.length !== 1 ? 's' : ''} detected`}
        </p>
        {defects.length > 0 && (
          <div className="space-y-1">
            {defects.map((defect, idx) => (
              <div key={idx} className="text-xs">
                <button
                  onClick={() => setExpandedDefect(expandedDefect === idx ? null : idx)}
                  className="flex items-center gap-1 w-full text-left hover:bg-zinc-700/50 rounded px-1 py-0.5"
                >
                  {expandedDefect === idx ? (
                    <ChevronDown className="w-3 h-3 text-zinc-500" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="w-3 h-3 text-zinc-500" aria-hidden="true" />
                  )}
                  <span className={SEVERITY_COLORS[defect.severity] || 'text-zinc-400'}>
                    {defect.severity}
                  </span>
                  <span className="text-zinc-300 ml-1">{defect.name}</span>
                </button>
                {expandedDefect === idx && defect.description && (
                  <p className="text-zinc-500 pl-5 py-1">{defect.description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ⛔ THE NOTICE IS RENDERED HERE, NOT BY EACH CONSUMER. VaultDetailPane and
          VaultUploadWithGrading both render this card; putting the disclaimer in the card is
          what guarantees neither can show a grade without it. #563 added the component and
          wired it to three OTHER surfaces, but not to the two vault surfaces that actually
          show a vault grade -- so the framing was absent exactly where a collector values a
          book. */}
      <GradeEstimateNotice />

      {/* Re-grade Button */}
      {onRegrade && (
        <button
          onClick={onRegrade}
          className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
        >
          <RefreshCw className="w-3 h-3" aria-hidden="true" />
          Re-grade
        </button>
      )}

      {/* CGC CTA */}
      {showCGCCta && (
        <CGCLoopCTA
          vaultItemId={vaultItemId}
          gradeValue={grade}
          confidence={conf}
        />
      )}
    </div>
  );
}
